import { FC, useEffect, useRef } from "react"
import { View, ViewStyle } from "react-native"
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withTiming,
  withDelay,
  Easing,
} from "react-native-reanimated"

import { colors } from "@/theme"

// ─── Confetti palette ──────────────────────────────────────────
// Exactly ONE particle is the saturated brand red (colors.primary); every other
// particle draws from NON_RED_CONFETTI so red stays the one-and-only brand
// accent in the celebration (DESIGN.md §2 Ten-Percent / one-red rule).
//
// IMPORTANT: do not pick the per-particle colour with `i % CONFETTI_COLORS.length`.
// With PARTICLE_COUNT (8) > palette length, the modulo wraps back onto index 0
// (red) and emits a SECOND red particle. Use colorForParticle() instead.
const NON_RED_CONFETTI = [
  colors.success,
  colors.confettiYellow,
  colors.confettiPink,
  colors.confettiPurple,
  colors.confettiCyan,
]

// Particle 0 is the single red stream; 1..n cycle over the non-red palette only.
const colorForParticle = (i: number): string =>
  i === 0 ? colors.primary : NON_RED_CONFETTI[(i - 1) % NON_RED_CONFETTI.length]

// Full palette (red first) — kept exported for any consumer that needs the set.
export const CONFETTI_COLORS = [colors.primary, ...NON_RED_CONFETTI]

export const PARTICLE_COUNT = 8

const getRandomAngle = (index: number) =>
  (index / PARTICLE_COUNT) * Math.PI * 2 + (Math.random() - 0.5) * 0.5

// ─── Single particle ───────────────────────────────────────────
// A radial burst: each particle eases outward along its angle while
// scaling and fading to zero. Works on web (it is an Animated.View driven
// by useAnimatedStyle, no native-only APIs).
const ConfettiParticle: FC<{ index: number; color: string }> = ({ index, color }) => {
  const progress = useSharedValue(0)
  const angle = useRef(getRandomAngle(index)).current
  const distance = useRef(60 + Math.random() * 40).current

  useEffect(() => {
    progress.value = withDelay(
      index * 50,
      withTiming(1, { duration: 800, easing: Easing.out(Easing.cubic) }),
    )
  }, [index, progress])

  const animatedStyle = useAnimatedStyle(() => {
    const x = Math.cos(angle) * distance * progress.value
    const y = Math.sin(angle) * distance * progress.value - 20 * progress.value
    return {
      transform: [{ translateX: x }, { translateY: y }, { scale: 1 - progress.value * 0.6 }],
      opacity: 1 - progress.value,
    }
  })

  return <Animated.View style={[$particle, { backgroundColor: color }, animatedStyle]} />
}

interface CelebrationBurstProps {
  /**
   * Number of particles to emit. Defaults to PARTICLE_COUNT (8). The first
   * particle is always the single saturated-red stream.
   */
  count?: number
}

/**
 * One-shot radial confetti burst, emitted from a zero-size anchor point.
 * Position the burst by wrapping it in a positioned container (e.g. an
 * absolutely-positioned 0×0 view centred on the celebration target).
 *
 * Shared by ReferralSurvey (thank-you screen) and the OrderBottomSheet
 * completion celebration so the particle physics live in exactly one place.
 *
 * Reduced-motion: callers must skip rendering this entirely (render nothing) —
 * the burst has no static fallback by design.
 */
export const CelebrationBurst: FC<CelebrationBurstProps> = ({ count = PARTICLE_COUNT }) => {
  return (
    <View style={$burstAnchor} pointerEvents="none">
      {Array.from({ length: count }).map((_, i) => (
        <ConfettiParticle key={i} index={i} color={colorForParticle(i)} />
      ))}
    </View>
  )
}

// ─── Styles ─────────────────────────────────────────────────────
const $burstAnchor: ViewStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  width: 0,
  height: 0,
  alignItems: "center",
  justifyContent: "center",
}

const $particle: ViewStyle = {
  position: "absolute",
  width: 8,
  height: 8,
  borderRadius: 4,
}
